import { defineStore } from 'pinia';
import type { LoyaltyHubContent } from '~/types/loyalty-hub';
import { fetchLoyaltyHubHandler } from '~/handlers/fetch-loyalty-hub.handler';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import {
  loyaltyHubCardArt,
  loyaltyHubHeaderArt,
  sortLoyaltyHubCards,
  sortLoyaltyHubCardsDesktop,
} from '~/utils/loyalty-hub';
import { vipTierStyle } from '~/utils/vip-tiers';
import { usePlayerProfileStore } from '~/stores/player-profile';

export type LoyaltyHubStatus = 'idle' | 'loading' | 'ready' | 'error';

// Quantas artes de card o `warmAssets` busca antes da abertura: só as que caem
// na primeira tela do painel. O resto entra `loading="lazy"` conforme a lista
// rola — aquecer as 9 seria ~600 KB especulativos pra quem talvez nem abra.
const WARM_CARDS = 2;

// Store do hub "Club Fidelidade": controla a abertura do modal global (sem
// navegar de rota, igual a roletinha) e o conteúdo dos cards. O cashback abre
// EMPILHADO por cima do hub, no mesmo formato de bottom-sheet da roletinha
// (CashbackSheet), com o conteúdo do cashback
// (`account.wallet.cashback_options_title`).
export const useLoyaltyHubStore = defineStore('loyalty-hub', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  // Tier de VIP: o aquecimento tem que pedir a MESMA arte que o card vai pedir,
  // senão o VIP baixa duas (a padrão no warm, a de tier no render).
  const { vipTier } = useVipStatus();

  const isOpen = ref(false);
  const isCashbackOpen = ref(false);

  // Slug do post (promoção do blog) aberto EMPILHADO por cima do hub, no
  // PostSheet. Só em memória, igual roletinha e cashback: nada na URL, então
  // não há deep-link nem entrada no histórico — fechar o modal não volta rota.
  const postSlug = ref<string | null>(null);

  // Prêmios (Rewards) abertos EMPILHADOS por cima do hub, no RewardsSheet. A
  // rota /user/rewards continua existindo e igual — o modal só reaproveita o
  // mesmo `<UserRewards />`. Também só em memória.
  const isRewardsOpen = ref(false);

  // Loja de Itens (VIP) empilhada por cima do hub, no StoreSheet. Mesma ideia do
  // rewards: a rota /vip/store continua existindo e igual, o modal só
  // reaproveita a mesma `<VipStoreV2 />` em modo embutido. Também só em memória.
  const isStoreOpen = ref(false);

  // Tela de níveis empilhada por cima do hub, no LevelsSheet. Mesma ideia do
  // rewards e da loja: a rota /vip/levels continua existindo e igual, o modal só
  // reaproveita as mesmas views. Fechar volta pro hub em vez de voltar rota —
  // que é o ponto de o clube parecer um lugar só. Também só em memória.
  const isLevelsOpen = ref(false);

  const status = ref<LoyaltyHubStatus>('idle');
  const content = ref<LoyaltyHubContent | null>(null);

  // Cards na ordem de exibição (`priority` menor primeiro, numa cópia — as
  // regras estão em utils/loyalty-hub). Cards desativados (`enabled: false`)
  // NÃO são movidos nem removidos: ficam na posição da priority deles, só que
  // bloqueados (o Card cuida do visual).
  const cards = computed(() => sortLoyaltyHubCards(content.value?.cards));

  // Posição (0-based) de cada card no grid DESKTOP, indexada por id. O layout de
  // 2 colunas do Figma distribui os cards numa ordem própria — o check-in abre a
  // lista e a loja desce pra linha de 3 — sem mexer na lista do mobile. Como a
  // ordem do DOM é a do mobile (`priority`), o desktop reordena por CSS `order`,
  // que é o que este mapa alimenta.
  // Ordena a partir da ordem do mobile (`priority`), então card sem
  // `desktopPriority` cai no fim mantendo a posição que tinha lá.
  const desktopOrder = computed<Record<string, number>>(() =>
    Object.fromEntries(
      sortLoyaltyHubCardsDesktop(content.value?.cards).map((card, index) => [
        card.id,
        index,
      ]),
    ),
  );

  // Deslogado não abre o hub: só o login. (O badge da barra só aparece logado,
  // mas deep-links/outros gatilhos podem chamar open() sem auth.)
  function open() {
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    isOpen.value = true;

    // Repergunta o /players/me na abertura. O plugin de sessão só dispara no
    // login e no foco da aba, e nenhum dos dois cobre "o jogador abriu o hub" —
    // que é justamente onde o nível VIP e a elegibilidade da roletinha estão na
    // tela. Sem isto o painel exibe o que sobrou da última resposta da sessão.
    //
    // Sem await de propósito: a abertura não espera a rede. Quem já tem valor
    // continua mostrando o que tinha e o card se corrige quando a resposta chega
    // (a store hidrata do cache da aba antes de perguntar — ver `ensure`).
    //
    // Abrir e fechar o hub em sequência não vira uma chamada por abertura: o
    // handler serve a última resposta dentro de uma janela curta. Este `ensure`
    // também é a rede de segurança de quando a chamada do login falhou — é por
    // isso que ele fica, mesmo com o plugin de sessão cobrindo login e foco.
    usePlayerProfileStore().ensure();
  }

  function close() {
    isOpen.value = false;
    // Fecha os sheets filhos junto — senão reabrem órfãos numa próxima abertura.
    isCashbackOpen.value = false;
    postSlug.value = null;
    isRewardsOpen.value = false;
    isStoreOpen.value = false;
    isLevelsOpen.value = false;
  }

  function openCashback() {
    isCashbackOpen.value = true;
  }

  function closeCashback() {
    isCashbackOpen.value = false;
  }

  function openPost(slug: string) {
    if (!slug) return;

    // Sem a barra inicial e sem o prefixo /blog/: o contrato manda o card com
    // `target: "/blog/<slug>"` (action 'route') OU só o slug (action 'blog'), e
    // o handler do post quer o slug puro.
    postSlug.value = slug.replace(/^\/?blog\//, '').replace(/^\//, '');
  }

  function closePost() {
    postSlug.value = null;
  }

  function openRewards() {
    isRewardsOpen.value = true;
  }

  function closeRewards() {
    isRewardsOpen.value = false;
  }

  function openStore() {
    isStoreOpen.value = true;
  }

  function closeStore() {
    isStoreOpen.value = false;
  }

  function openLevels() {
    isLevelsOpen.value = true;
  }

  function closeLevels() {
    isLevelsOpen.value = false;
  }

  // Conteúdo pesado carregado só no after-enter do slide-up (mesma estratégia
  // da roletinha), pra não competir com a animação de abertura.
  async function init() {
    if (status.value === 'loading') return;

    status.value = 'loading';

    const [data, error] = await tryCatch(fetchLoyaltyHubHandler());

    if (error || !data) {
      status.value = 'error';

      return;
    }

    content.value = data;
    status.value = 'ready';
  }

  // Aquece as artes do topo do hub (header + primeiros cards) no ócio do
  // navegador, antes de qualquer abertura. O conteúdo em si é bundlado e chega
  // de graça; o que custava era a rede das imagens: o painel montava os cards
  // com a arte ainda em trânsito e ia se acomodando conforme cada webp caía.
  // A reserva de caixa (`aspect-ratio` no Card) já tira o pulo do layout — isto
  // aqui é pro conteúdo chegar PRONTO na primeira abertura.
  // NÃO popula `content`: o carregamento segue no @after-enter do slide-up, pra
  // montagem dos cards não competir com a animação.
  let warmed = false;

  async function warmAssets() {
    if (warmed || !import.meta.client) return;

    warmed = true;

    const [data] = await tryCatch(fetchLoyaltyHubHandler());

    if (!data) return;

    // A arte é escolhida por breakpoint no <picture>; aquecer a do outro lado
    // seria download jogado fora. Quem abre a lista também muda por breakpoint
    // (o desktop tem ordem própria), então a fila do aquecimento acompanha.
    const desktop = window.matchMedia('(min-width: 1024px)').matches;

    const ordered = desktop
      ? sortLoyaltyHubCardsDesktop(data.cards)
      : sortLoyaltyHubCards(data.cards);

    // Card com versão de tier aquece o badge + a roda dele, e não a arte estática:
    // o VIP nunca vai pedir a estática, e aquecer as duas coisas seria baixar
    // ~50 KB pra jogar fora. Vale nos DOIS breakpoints — o card composto tem
    // desenho mobile e desktop, e os assets são os mesmos nos dois.
    const vipStyle = vipTierStyle(vipTier.value);

    const urls = [
      loyaltyHubHeaderArt(data, desktop),
      ...ordered
        .slice(0, WARM_CARDS)
        .flatMap((card) =>
          card.vipTiers === true && vipStyle
            ? [vipStyle.badge, vipStyle.wheel]
            : [loyaltyHubCardArt(card, desktop)],
        ),
    ].filter(Boolean);

    urls.forEach((url) => {
      const img = new Image();

      img.decoding = 'async';
      img.src = url;
    });
  }

  return {
    isOpen,
    isCashbackOpen,
    postSlug,
    isRewardsOpen,
    isStoreOpen,
    isLevelsOpen,
    status,
    content,
    cards,
    desktopOrder,
    open,
    close,
    openCashback,
    closeCashback,
    openPost,
    closePost,
    openRewards,
    closeRewards,
    openStore,
    closeStore,
    openLevels,
    closeLevels,
    init,
    warmAssets,
  };
});
